/**
 * index.js — /car-usb-music. Сборка флешки, которую автомагнитола прочитает.
 *
 * Порядок работы тула повторяет порядок причин, по которым флешка не играет:
 *
 *   1. РАЗБОР. У каждого файла спрашивается, что с ним не так ИМЕННО для
 *      головного устройства (verdict.js). Ответ показывается человеку —
 *      он же объясняет, почему один трек готов мгновенно, а другой минуту.
 *   2. ЧИНИТСЯ ПОБАЙТОВО ВСЁ, ЧТО МОЖНО. Теги, ID3v1, обложка, заголовок VBR,
 *      громкость — без перекодирования, то есть без потери качества и без
 *      тридцати секунд FFmpeg на трек.
 *   3. ПЕРЕКОДИРУЕТСЯ ТОЛЬКО НЕИГРАБЕЛЬНОЕ. FLAC, AAC, OGG, MPEG-2 —
 *      то, чего устройство физически не умеет. WMA — по выбору человека
 *      (с 24.09.2026): многие магнитолы его играют, и тогда он ложится на
 *      флешку как есть, с переписанными тегами (wma-car.js).
 *   4. ПИШЕТСЯ ПО ОДНОМУ ФАЙЛУ В ПОРЯДКЕ ВОСПРОИЗВЕДЕНИЯ. Это и есть починка
 *      «играет вперемешку»: FAT32 не сортирует каталог, он отдаёт записи в
 *      порядке создания.
 *
 * ⚠️ Пункт 4 работает только через File System Access API (запись прямо на
 * флешку). Архив порядок каталога НЕ задаёт — его определит распаковщик.
 * Поэтому архивная ветка честно об этом предупреждает, а не делает вид, что
 * результат тот же.
 */

import { analyzeMp3, applyGainDb, compactStream, ensureVbrHeader, GAIN_STEP_DB, id3v2Length, isWritableMp3, looksLikeMpegStream } from './mp3-car.js';
import { retagMp3, buildId3v2, buildId3v1, latin1Fits } from './id3-car.js';
import { buildPlan, buildM3u, appendM3u, sortItems, safeName, explorerCompare, takenNumbers, FAT32_MAX_FILE } from './plan.js';
import { decide, PROFILES, transcodeArgs } from './verdict.js';
import { analyzeWma, rewriteWmaTags, isWritableWma, wmaRejectReason } from './wma-car.js';
import { integratedLoudness, ROAD_TARGET_LUFS } from './loudness.js';
import { transliterate } from './translit.js';
import { readCover } from '../audio-cover.js';
import { asfHeaderSize } from '../asf-meta.js';
import { walkFolder, listFolder, topExtensions } from './folder-walk.js';

const SLUG = 'car-usb-music';

/* Форматы, которые вообще имеет смысл принимать. Всё, кроме mp3, приедет в
   ветку перекодирования — и это нормально: человек не обязан знать, что у него
   лежит FLAC. */
const AUDIO_EXT = ['mp3', 'flac', 'wav', 'm4a', 'aac', 'ogg', 'oga', 'opus', 'wma', 'aiff', 'aif', 'ape', 'alac', 'mp2', 'mpga', 'wv'];

/* Метка трека, который не поместился: флешка кончилась раньше списка. Это не
   «повреждённый файл», и отчёт говорит о нём отдельно. */
const NO_SPACE = 'no_space';

let ui = null;
let items = [];
let seq = 0;
let running = false;
let lastPlan = null;

const settings = {
    profile: 'common',
    mode: 'ascii',
    order: 'as-is',
    numbering: true,
    nameFrom: 'tags',
    folders: 'flat',
    cover: 'shrink',
    level: false,
    playlist: true,
    bitrate: 192,
    wma: 'mp3',
};

/* ───────────────────────────── маленькие помощники ─────────────────────── */

const $ = (id) => document.getElementById(id);
const t = (k, sub) => {
    const s = (ui && ui.lang && ui.lang[k]) || k;
    if (!sub) return s;
    /* Один проход по меткам. Замена по очереди давала «:n» съесть начало
       «:name»: строка записи выходила «Записываю 5 из 755 — 755ame»
       (до 25.09.2026), а имя файла с двоеточием разбиралось бы повторно. */
    return s.replace(/:([A-Za-z_]+)/g, (m, p) => (Object.prototype.hasOwnProperty.call(sub, p) ? String(sub[p]) : m));
};
/* Воронка. `__stage` сам считает время на шаге и досылает `final_stage`,
   когда человек уходит со страницы, — то есть отвечает на вопрос «где
   отваливаются», а не «сколько нажали». Слаг берётся из <meta name="tool-slug">,
   передавать его не надо. */
const stage = (n, d) => { try { window.__stage && window.__stage(n, d || {}); } catch (e) {} };
const ae = (ev, data) => { try { window.__ae && window.__ae(ev, Object.assign({ slug: SLUG }, data || {})); } catch (e) {} };
const fmtMb = (b) => (b / 1048576).toFixed(b < 10485760 ? 1 : 0) + ' MB';
const fmtTime = (s) => {
    if (!isFinite(s) || s <= 0) return '';
    const m = Math.floor(s / 60), ss = Math.round(s % 60);
    return m + ':' + (ss < 10 ? '0' : '') + ss;
};
const extOf = (name) => {
    const i = String(name || '').lastIndexOf('.');
    return i > 0 ? name.slice(i + 1).toLowerCase() : '';
};

async function bytesOf(file) {
    if (window.FileBytes && window.FileBytes.arrayBuffer) {
        return new Uint8Array(await window.FileBytes.arrayBuffer(file));
    }
    return new Uint8Array(await file.arrayBuffer());
}

/* ─────────────────────────────── разбор файла ──────────────────────────── */

const TAG_KEYS = {
    TIT2: 'title', TT2: 'title', TITLE: 'title', '©nam': 'title',
    TPE1: 'artist', TP1: 'artist', ARTIST: 'artist', '©ART': 'artist',
    TPE2: 'albumArtist', ALBUMARTIST: 'albumArtist', aART: 'albumArtist',
    TALB: 'album', TAL: 'album', ALBUM: 'album', '©alb': 'album',
    TRCK: 'track', TRK: 'track', TRACKNUMBER: 'track', trkn: 'track',
    TPOS: 'disc', DISCNUMBER: 'disc', disk: 'disc',
    TYER: 'year', TDRC: 'year', TYE: 'year', DATE: 'year', '©day': 'year',
    TCON: 'genre', TCO: 'genre', GENRE: 'genre', '©gen': 'genre',
    TCOM: 'composer', COMPOSER: 'composer',
    // WMA (ASF): Content Description и WM/*-поля. Сверка идёт и по верхнему
    // регистру, поэтому хватает одной записи на имя.
    AUTHOR: 'artist', 'WM/ALBUMTITLE': 'album', 'WM/ALBUMARTIST': 'albumArtist',
    'WM/TRACKNUMBER': 'track', 'WM/PARTOFSET': 'disc', 'WM/YEAR': 'year',
    'WM/GENRE': 'genre', 'WM/COMPOSER': 'composer',
};

async function readTags(file) {
    const out = {};
    try {
        if (!window.AudioTagReader) return out;
        const r = await window.AudioTagReader.read(file);
        for (const f of (r.fields || [])) {
            /* ⚠️ Имя поля лежит в `id`, а НЕ в `key`: у текстовых фреймов ID3
               общий читатель кладёт в `key` пустую строку (`key` там занят
               описанием TXXX/COMM). Вычитано не из документации, а из прогона:
               FLAC подхватывался, MP3 молча приезжал без единого тега. */
            const raw = f.id || f.name || f.key;
            const key = TAG_KEYS[raw] || TAG_KEYS[String(raw || '').toUpperCase()];
            if (!key || out[key]) continue;
            const v = String(f.value == null ? '' : f.value);
            if (v.trim()) out[key] = v;
        }
        repairEncoding(out);
        for (const k in out) { out[k] = out[k].trim(); if (!out[k]) delete out[k]; }
    } catch (e) { /* тег не обязателен */ }
    return out;
}

/* Тег, записанный не той кодировкой, что объявлена, — до всего остального.
   Половина русской фонотеки собрана в нулевые и лежит в cp1251 или UTF-8 внутри
   однобайтного слота («Ãðóïïà êðîâè», «ÐšÐ¸Ð½Ð¾»); читать её «по спецификации»
   значит записать на флешку ту же кашу, только необратимо.

   Решает общий модуль (public/js/tag-text-encoding.js) — по всем полям файла сразу
   и только там, где уверен: здесь человек кодировку не выбирает, на флешку уходит
   то, что прочитано. До 07.10.2026 решало собственное правило «в строке две буквы
   À–ÿ и больше — значит, кириллица», и оно портило западные названия: «Déjà Vu»
   уезжало в машину как «Dйjа Vu», «Mötley Crüe» — как «Mцtley Crьe». */
const TEXT_TAGS = ['title', 'artist', 'albumArtist', 'album', 'genre', 'composer'];
function repairEncoding(tags) {
    const T = window.TagTextEncoding;
    if (!T || !T.repair) return;
    const keys = TEXT_TAGS.filter(k => typeof tags[k] === 'string');
    if (!keys.length) return;
    const r = T.repair(keys.map(k => tags[k]), { locale: window.__LOCALE });
    if (!r.changed) return;
    keys.forEach((k, i) => { tags[k] = r.texts[i]; });
    ae('feature_use', { action: 'tag_encoding_fixed', enc: r.enc });
}

/** Размер встроенной обложки — без её чтения целиком. */
function coverBytesOf(u8, info) {
    if (!info || !info.id3v2Bytes) return 0;
    // APIC — самый крупный фрейм; берём сумму APIC-фреймов внутри ID3v2.
    let p = 10, total = 0;
    const end = Math.min(info.id3v2Bytes, u8.length) - 10;
    while (p < end) {
        const id = String.fromCharCode(u8[p], u8[p + 1], u8[p + 2], u8[p + 3]);
        const sz = (u8[p + 4] << 24) | (u8[p + 5] << 16) | (u8[p + 6] << 8) | u8[p + 7];
        if (!/^[A-Z0-9]{4}$/.test(id) || sz <= 0) break;
        if (id === 'APIC' || id === 'PIC') total += sz;
        p += 10 + sz;
    }
    return total;
}

/** Вытащить байты обложки (для уменьшения или переноса). */
function extractCover(u8, info) {
    if (!info || !info.id3v2Bytes) return null;
    let p = 10;
    const end = Math.min(info.id3v2Bytes, u8.length) - 10;
    while (p < end) {
        const id = String.fromCharCode(u8[p], u8[p + 1], u8[p + 2], u8[p + 3]);
        const sz = (u8[p + 4] << 24) | (u8[p + 5] << 16) | (u8[p + 6] << 8) | u8[p + 7];
        if (!/^[A-Z0-9]{4}$/.test(id) || sz <= 0) break;
        if (id === 'APIC') {
            const body = u8.subarray(p + 10, p + 10 + sz);
            let i = 1, mime = '';
            while (i < body.length && body[i] !== 0) mime += String.fromCharCode(body[i++]);
            i++;                       // терминатор mime
            i++;                       // тип картинки
            while (i < body.length && body[i] !== 0) i++;   // описание
            i++;
            if (i < body.length) return { mime: mime || 'image/jpeg', data: body.subarray(i).slice() };
        }
        p += 10 + sz;
    }
    return null;
}

/** Четыре байта начала полезной нагрузки — чем оказался файл «.mp3» на деле. */
function headMagic(u8) {
    const at = id3v2Length(u8);
    const ascii = (o) => String.fromCharCode(u8[o], u8[o + 1], u8[o + 2], u8[o + 3]).replace(/[^\x20-\x7e]/g, '.');
    if (u8.length >= at + 8 && ascii(at + 4) === 'ftyp') return 'ftyp';
    return u8.length >= at + 4 ? ascii(at) : '';
}

/* Байты для разбора. Вердикту по WMA нужен только заголовок ASF и начало
   объекта данных — не 5–10 МБ звука: в фонотеке заявителя (26.09.2026) WMA
   были 526 треков из 755, и разбор читал их целиком, хотя полностью файл нужен
   лишь при записи. Не ASF (переименованный MP3 и т. п.) — читается целиком,
   как раньше. */
async function probeBytes(it) {
    if (it.ext === 'wma' || it.ext === 'asf') {
        const head = new Uint8Array(await it.file.slice(0, 30).arrayBuffer());
        const hs = asfHeaderSize(head);
        if (hs && hs + 64 < it.size) return new Uint8Array(await it.file.slice(0, hs + 64).arrayBuffer());
    }
    return bytesOf(it.file);
}

async function probeItem(it) {
    const u8 = await probeBytes(it);
    it.info = (it.ext === 'mp3' || it.ext === 'mp2' || it.ext === 'mpga') ? analyzeMp3(u8) : null;
    /* Файл назван .mp3, а потока MPEG в нём нет: M4A или FLAC, которому
       загрузчик дал чужое расширение. Такой трек уходит в перекодирование
       (вердикт «не MP3»), а событие считает класс, которого раньше не было
       видно вовсе: до 23.09.2026 такие файлы принимались за MP3 и уезжали на
       флешку неиграбельными. */
    if (it.ext === 'mp3' && !it.info) ae('feature_use', { action: 'mp3_not_mpeg', head: headMagic(u8), mb: Math.round(it.size / 1048576) });
    /* WMA узнаётся по заголовку ASF, а не по «.wma»: кодек (обычный WMA или
       Pro/Lossless, которые магнитолы не играют) и защита DRM решают, можно
       ли оставить файл как есть. */
    it.wma = it.info ? null : analyzeWma(u8);
    if (!it.info && !it.wma && it.ext === 'wma') it.wmaReject = wmaRejectReason(u8);
    it.coverBytes = it.wma ? it.wma.pictureBytes : coverBytesOf(u8, it.info);
    it.tags = await readTags(it.file);
    if (!it.tags.title) it.tags.title = it.name.replace(/\.[^.]+$/, '');
    it.durationSec = it.info ? it.info.durationSec : (it.wma ? it.wma.durationSec : 0);
    recompute(it);
    return it;
}

function recompute(it) {
    it.verdict = decide(it.info, {
        ext: it.ext, sizeBytes: it.size, coverBytes: it.coverBytes, wma: it.wma || null,
    }, { profile: settings.profile, mode: settings.mode, levelVolume: settings.level, keepWma: settings.wma === 'keep' });
}

/* ────────────────────────────── добавление файлов ──────────────────────── */

/* Папка файла относительно выбранного каталога — для «Папки: как в исходной
   папке». Из обхода папки приходит {file, path}; у файла, выбранного в
   пикере с папкой, есть webkitRelativePath; у одиночного файла — пусто. */
const dirOfPath = (p) => { const s = String(p || ''); const i = s.lastIndexOf('/'); return i > 0 ? s.slice(0, i) : ''; };

async function addFiles(files, via) {
    const recs = Array.from(files || []).map((f) => (f && f.file
        ? { file: f.file, dir: dirOfPath(f.path) }
        : { file: f, dir: dirOfPath(f && f.webkitRelativePath && f.webkitRelativePath.split('/').slice(1).join('/')) }));
    const list = recs.filter((r) => r.file && AUDIO_EXT.indexOf(extOf(r.file.name)) >= 0);
    if (!list.length) {
        window.__toast && window.__toast(t('err_no_audio'), 'warning');
        return;
    }
    ui.stage.hidden = false;
    setBusy(true, t('busy_reading_n', { i: 0, n: list.length }));
    let n = 0;
    /* Таблица перерисовывается целиком, поэтому «каждые 8 файлов» на фонотеке
       в тысячи треков — это сотни полных перерисовок растущей таблицы.
       Перерисовка по времени держит цену постоянной. */
    let painted = Date.now();
    const added = [];
    for (const { file: f, dir } of list) {
        const it = {
            id: 'i' + (++seq), file: f, name: f.name, ext: extOf(f.name), size: f.size, dir,
            tags: {}, info: null, coverBytes: 0, lufs: null, gainDb: 0, status: 'pending',
            // Ключ капа штампуется ОДИН раз при добавлении. Выводить его из
            // живого File на каждое нажатие нельзя: тул может подменить File
            // результатом, и тот же трек станет «новым файлом» — за это уже
            // платили на /image-metadata и /video-metadata.
            limitKey: f.name + '|' + f.size + '|' + (f.lastModified || 0),
        };
        /* Файл, который браузер не прочёл (занят другой программой, облачная
           заглушка, сбойный сектор), остаётся в списке с пометкой и причиной —
           а не валит потом весь запуск записи: так 25.09.2026 три запуска подряд
           упали на одном треке, пока человек сам не нашёл и не удалил строку. */
        try { await probeItem(it); } catch (e) { it.readErr = String(e && e.message || e); }
        items.push(it);
        added.push(it);
        n++;
        if (Date.now() - painted > 700) {
            painted = Date.now();
            ui.busyText.textContent = t('busy_reading_n', { i: n, n: list.length });
            renderList();
            await new Promise((r) => setTimeout(r, 0));
        }
    }
    setBusy(false);
    applyOrder();
    renderAll();
    stage('files_added', { n: items.length });
    const unreadable = added.filter((it) => it.readErr).length;
    // «Нет доступа» (NotReadableError: права Windows или файл занят) против прочих отказов.
    const unreadAccess = added.filter((it) => it.readErr && /NotReadable|permission/i.test(it.readErr)).length;
    ae('feature_use', { action: 'add_files', n: list.length, total: items.length, via: via || 'zone', unreadable, unread_access: unreadAccess });
    reportWma(added);
}

/* Сколько WMA приходит и какого — чтобы решение «оставить или перекодировать»
   опиралось на данные, а не на пересказ инструкций магнитол. Одним событием на
   добавление, а не по событию на трек: сюда приносят фонотеки на сотни файлов. */
function reportWma(added) {
    const w = added.filter((it) => it.wma).map((it) => it.wma);
    const rej = added.filter((it) => it.wmaReject).map((it) => it.wmaReject);
    if (!w.length && !rej.length) return;
    const n = (f) => w.filter(f).length;
    const r = (why) => rej.filter((x) => x === why).length;
    ae('feature_use', {
        action: 'wma_seen', n: w.length,
        std: n((x) => x.codec === 'wma2' || x.codec === 'wma1'),
        pro: n((x) => x.codec === 'wmapro'), lossless: n((x) => x.codec === 'wmalossless'),
        voice: n((x) => x.codec === 'wmavoice'), other: n((x) => /other|mixed/.test(x.codec)),
        drm: n((x) => x.drm), setting: settings.wma,
        // «.wma», который WMA не оказался: иначе он уходил в MP3 невидимо.
        rej_video: r('video'), rej_broken: r('broken'), rej_not_asf: r('not_asf'), rej_no_audio: r('no_audio'),
    });
}

/** Импорт целой папки — так музыка и лежит у человека на диске. */
async function importFolder() {
    if (!window.showDirectoryPicker) {
        window.__toast && window.__toast(t('err_no_fs_api'), 'warning');
        return;
    }
    let dir;
    try { dir = await window.showDirectoryPicker({ mode: 'read' }); }
    catch (e) { return; }                        // отмена — не ошибка
    setBusy(true, t('busy_scanning'));
    const t0 = Date.now();
    let painted = 0;
    /* Отказ папки или файла больше не обрывает обход (folder-walk.js): до
       25.09.2026 один try вокруг всей рекурсии молча отдавал «что успели», и
       из каталога на 32 ГБ в таблицу попадало 6,59 ГБ без единого слова. */
    const scan = await walkFolder(dir, {
        accept: (ext) => AUDIO_EXT.indexOf(ext) >= 0,
        maxFiles: MAX_FOLDER_FILES,
        onProgress: (n) => {
            if (Date.now() - painted < 400) return;
            painted = Date.now();
            ui.busyText.textContent = t('busy_scanning_n', { n });
        },
    });
    setBusy(false);
    showScanNote(scan);
    reportScan(scan, Date.now() - t0);
    /* Порядок — тот, в котором человек видит папку в проводнике, а не порядок
       записей каталога: браузер отдаёт его как лежит на диске («1, 10, 2…» на
       NTFS, порядок копирования на FAT32/exFAT). Обращение 25.09.2026: «треки
       внутри альбома становятся совсем не так, как нужно». Проверка готовой
       флешки (auditFolder) нарочно читает СЫРОЙ порядок — она его и судит. */
    const inOrder = scan.files.slice().sort((a, b) => explorerCompare(a.path, b.path));
    ae('feature_use', { action: 'folder_order', moved: inOrder.filter((f, i) => f !== scan.files[i]).length, n: inOrder.length });
    await addFiles(inOrder.map((f) => ({ file: f.file, path: f.path })), 'folder');
}

/* Сколько файлов берёт один обход. Прежний потолок 3000 срезал фонотеку в
   30 ГБ; 20 000 — это больше 100 ГБ музыки, больше любой флешки для машины. */
const MAX_FOLDER_FILES = 20000;

const fmtBytes = (b) => (b >= 1073741824 ? (b / 1073741824).toFixed(2) + ' GB' : fmtMb(b));
const namesOf = (list, k) => list.slice(0, k).map((x) => escapeHtml(x.path || x))
    .join(', ') + (list.length > k ? ', …' : '');

/**
 * Что обход ВЗЯЛ и что ОСТАВИЛ — прямо под кнопкой, до таблицы. Разница
 * между «в папке 32 ГБ» и «в списке 6,59 ГБ» должна объясняться здесь, а не
 * обнаруживаться в машине.
 */
function showScanNote(scan) {
    if (!ui.scan) return;
    const lines = [`<div class="cu-rep-facts">📁 ${t('scan_found', { n: scan.files.length, s: fmtBytes(scan.bytes) })}</div>`];
    if (scan.other.n) {
        const list = topExtensions(scan.other.byExt, 6).map(([e, n]) => '.' + escapeHtml(e) + ' ' + n).join(' · ');
        lines.push(`<div class="cu-rep-facts">${t('scan_other', { n: scan.other.n, s: fmtBytes(scan.other.bytes), list })}</div>`);
    }
    if (scan.dirErrors.length) lines.push(`<div class="cu-warn">⚠️ ${t('scan_dir_err', { n: scan.dirErrors.length, names: namesOf(scan.dirErrors, 5) })}</div>`);
    if (scan.fileErrors.length) lines.push(`<div class="cu-warn">⚠️ ${t('scan_file_err', { n: scan.fileErrors.length, names: namesOf(scan.fileErrors, 5) })}</div>`);
    if (scan.system.length) lines.push(`<div class="cu-rep-facts">${t('scan_system', { names: namesOf(scan.system, 4) })}</div>`);
    if (scan.depthCut) lines.push(`<div class="cu-warn">⚠️ ${t('scan_deep', { n: scan.depthCut })}</div>`);
    if (scan.capped) lines.push(`<div class="cu-warn">⚠️ ${t('scan_capped', { n: MAX_FOLDER_FILES, rest: scan.capped })}</div>`);
    ui.scan.innerHTML = lines.join('');
    ui.scan.hidden = false;
}

/* Одно событие на обход: чего именно не хватило. Пути не отправляются —
   только число, имена ошибок и расширения. */
function reportScan(scan, ms) {
    const errNames = (list) => Array.from(new Set(list.map((x) => x.err))).join(',').slice(0, 60);
    ae('feature_use', {
        action: 'folder_scan', found: scan.files.length, mb: Math.round(scan.bytes / 1048576),
        dirs: scan.dirs, other: scan.other.n, other_mb: Math.round(scan.other.bytes / 1048576),
        other_top: topExtensions(scan.other.byExt, 5).map(([e, n]) => e + ':' + n).join(',').slice(0, 80),
        dir_err: scan.dirErrors.length, dir_err_names: errNames(scan.dirErrors),
        file_err: scan.fileErrors.length, file_err_names: errNames(scan.fileErrors),
        system: scan.system.length, deep: scan.depthCut, capped: scan.capped, ms,
    });
}

/* ─────────────────────────────── план и отрисовка ──────────────────────── */

function applyOrder() {
    if (settings.order && settings.order !== 'as-is') items = sortItems(items, settings.order);
}

function buildCurrentPlan(numberFrom) {
    const P = PROFILES[settings.profile];
    /* Пропущенный трек (DRM) номера не получает: иначе в нумерации «01, 02,
       04…» осталась бы дыра на месте файла, которого на флешке нет. */
    lastPlan = buildPlan(items.filter((it) => !isSkipped(it) && !it.readErr).map((it) => ({
        id: it.id, tags: it.tags, sourceName: it.name, dir: it.dir || '',
        ext: (it.verdict && it.verdict.out) || 'mp3',
        sizeBytes: it.size,
    })), {
        mode: settings.mode,
        numbering: settings.numbering,
        nameFrom: settings.nameFrom,
        folders: settings.folders,
        maxPerFolder: P.maxPerFolder,
        maxNameLen: P.maxNameLen,
        numberFrom: numberFrom || null,
    });
    return lastPlan;
}

/** Прежний плейлист в папке записи — байты или null. Читается ДО перезаписи. */
async function readPriorPlaylist(dir, name) {
    try {
        const f = await (await dir.getFileHandle(name)).getFile();
        return f.size > 0 && f.size < 8 * 1048576 ? new Uint8Array(await f.arrayBuffer()) : null;
    } catch (e) { return null; }
}

/**
 * Что уже лежит в папке, выбранной для записи. Флешку дописывают: «добавить
 * новые треки» — обычный второй шаг (обращение 26.09.2026). Считаем, что там
 * уже есть, и какие номера заняты в каждой папке плана — чтобы новые треки
 * продолжили нумерацию, а не легли вторыми «01».
 */
async function lookAtDestination(dir, plan) {
    const top = await listFolder(dir);
    const out = {
        files: top.files.filter((n) => AUDIO_EXT.indexOf(extOf(n)) >= 0).length,
        dirs: top.dirs,
        numberFrom: Object.create(null),
    };
    for (const f of new Set(plan.entries.map((e) => e.folder || ''))) {
        let h = dir;
        try { for (const seg of f.split('/').filter(Boolean)) h = await h.getDirectoryHandle(seg); }
        catch (e) { continue; }                     // папки ещё нет — номера с «01»
        const taken = takenNumbers(h === dir ? top.files : (await listFolder(h)).files);
        if (taken) out.numberFrom[f] = taken;
    }
    return out;
}

const isSkipped = (it) => !!(it.verdict && it.verdict.lane === 'skip');

const REASON_KEY = {
    not_mp3: 'why_not_mp3', layer: 'why_layer', mpeg2: 'why_mpeg2',
    sample_rate: 'why_rate', free_format: 'why_free', bitrate: 'why_bitrate', vbr: 'why_vbr',
    wma: 'why_wma', wma_pro: 'why_wma_pro', wma_lossless: 'why_wma_lossless', wma_voice: 'why_wma_voice',
    wma_codec: 'why_wma_codec', drm: 'why_drm',
};
const FIX_KEY = {
    retag: 'fix_retag', add_id3v1: 'fix_id3v1', shrink_cover: 'fix_cover',
    add_xing: 'fix_xing', strip_junk: 'fix_junk', inner_junk: 'fix_inner_junk', gain: 'fix_gain',
};
const NOTE_KEY = { no_gain: 'note_no_gain' };
const LANE_KEY = { lossless: 'lane_lossless', transcode: 'lane_transcode', skip: 'lane_skip' };
/* Подпись кодека в строке трека: «WMA» — это обычный WMA 7/8/9, остальные
   называются по имени, потому что именно имя решает, сыграет ли магнитола. */
const WMA_LABEL = { wma1: 'WMA', wma2: 'WMA', wmapro: 'WMA Pro', wmalossless: 'WMA Lossless', wmavoice: 'WMA Voice' };

function renderList() {
    const plan = buildCurrentPlan();
    const byId = Object.create(null);
    for (const e of plan.entries) byId[e.id] = e;

    let html = '';
    items.forEach((it, i) => {
        const e = byId[it.id];
        const lane = it.verdict ? it.verdict.lane : 'lossless';
        const badges = [];
        if (it.verdict) {
            for (const r of it.verdict.reasons) badges.push(`<span class="cu-badge cu-badge-re">${t(REASON_KEY[r] || r)}</span>`);
            for (const f of it.verdict.fixes) {
                if (f === 'retag') continue;               // это делается всегда, не новость
                badges.push(`<span class="cu-badge cu-badge-fix">${t(FIX_KEY[f] || f)}</span>`);
            }
            // Причина — во всплывающей подсказке: «громкость не выравнивается» без
            // «почему» читалось как поломка (обращение 25.09.2026).
            for (const nt of (it.verdict.notes || [])) badges.push(`<span class="cu-badge cu-badge-re"${nt === 'no_gain' ? ` title="${escapeHtml(t('level_wma_note'))}"` : ''}>${t(NOTE_KEY[nt] || nt)}</span>`);
        }
        /* Итог записи — первым бейджем: какой трек не лёг и какой лёг как есть
           вместо перекодирования. Причина — во всплывающей подсказке. */
        if (it.readErr) badges.unshift(`<span class="cu-badge cu-badge-err" title="${escapeHtml(t('row_unreadable_why'))}">${t('row_unreadable')}</span>`);
        else if (it.status === 'error' && it.error === NO_SPACE) badges.unshift(`<span class="cu-badge cu-badge-err">${t('row_no_space')}</span>`);
        else if (it.status === 'error') badges.unshift(`<span class="cu-badge cu-badge-err" title="${escapeHtml(it.error || '')}">${t('row_failed')}</span>`);
        else if (it.status === 'done' && it.fallback) badges.unshift(`<span class="cu-badge cu-badge-re" title="${escapeHtml(it.fallback)}">${t('row_fallback')}</span>`);
        if (!badges.length) badges.push(`<span class="cu-badge cu-badge-ok">${t('badge_ok')}</span>`);

        const meta = [];
        if (it.info) {
            meta.push(`${it.info.sampleRate / 1000} kHz`);
            meta.push(it.info.vbr ? `VBR ~${it.info.bitrateAvg}` : `${it.info.bitrateAvg} kbps`);
            if (it.info.channels === 1) meta.push(t('mono'));
        } else if (it.wma) {
            meta.push(WMA_LABEL[it.wma.codec] || 'ASF');
            if (it.wma.sampleRate) meta.push(`${it.wma.sampleRate / 1000} kHz`);
            if (it.wma.bitrateKbps) meta.push(`${it.wma.bitrateKbps} kbps`);
            if (it.wma.channels === 1) meta.push(t('mono'));
        } else meta.push(it.ext.toUpperCase());
        if (it.durationSec) meta.push(fmtTime(it.durationSec));
        meta.push(fmtMb(it.size));
        if (it.lufs != null && isFinite(it.lufs)) meta.push(it.lufs.toFixed(1) + ' LUFS');

        html += `<tr data-id="${it.id}" draggable="true" class="cu-row${it.status === 'done' ? ' is-done' : ''}${(it.status === 'error' || it.readErr) ? ' is-failed' : ''}">
            <td class="cu-num">${i + 1}</td>
            <td class="cu-src">
                <div class="cu-src-name" title="${escapeHtml(it.name)}">${escapeHtml(it.name)}</div>
                <div class="cu-src-meta">${meta.join(' · ')}</div>
            </td>
            <td class="cu-out">
                <div class="cu-out-name">${escapeHtml(e ? e.path : '—')}</div>
                <div class="cu-flags">${badges.join(' ')}</div>
            </td>
            <td class="cu-lane">${it.readErr ? '—' : `<span class="cu-lane-${lane}">${t(LANE_KEY[lane] || 'lane_lossless')}</span>`}</td>
            <td class="cu-act"><button type="button" class="cu-x" data-del="${it.id}" aria-label="${t('remove')}">×</button></td>
        </tr>`;
    });
    ui.tbody.innerHTML = html;
    ui.count.textContent = items.length;
}

function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[c]);
}

function renderSummary() {
    const plan = lastPlan || buildCurrentPlan();
    const total = items.reduce((s, it) => s + it.size, 0);
    const trans = items.filter((it) => it.verdict && it.verdict.lane === 'transcode').length;
    const dur = items.reduce((s, it) => s + (it.durationSec || 0), 0);

    const bits = [];
    bits.push(t('sum_tracks', { n: items.length }));
    if (dur > 0) bits.push(t('sum_duration', { d: fmtTime(dur) }));
    bits.push(t('sum_size', { s: fmtMb(total) }));
    if (plan.folders) bits.push(t('sum_folders', { n: plan.folders }));
    if (trans) bits.push(t('sum_transcode', { n: trans }));
    ui.summary.textContent = bits.join(' · ');

    const warn = [];
    for (const w of plan.warnings) {
        if (w.code === 'folder_split') warn.push(t('warn_split', { n: w.parts, limit: w.limit }));
        if (w.code === 'over_fat32') warn.push(t('warn_fat32'));
    }
    if (items.length > 999 && settings.folders === 'flat' && PROFILES[settings.profile].maxPerFolder === 0) {
        warn.push(t('warn_many_files'));
    }
    /* Файлы, которые браузер не смог прочитать, — с причиной и починкой, а не
       «FILE_LOST» во всплывающей подсказке. Чаще всего у файла нет прав на
       чтение для учётной записи Windows (обращение 26.09.2026: «Проводник при
       копировании просил права администратора, AIMP их не читает»); прочесть
       его не может никакая программа этого пользователя, поэтому «добавить
       всё равно» невозможно — лечится правами. */
    const unread = items.filter((it) => it.readErr);
    if (unread.length) {
        const names = unread.slice(0, 5).map((it) => escapeHtml(it.name)).join(', ') + (unread.length > 5 ? ', …' : '');
        warn.push(t('warn_unreadable', { n: unread.length, names }));
    }
    ui.warn.innerHTML = warn.length ? warn.map((w) => `<div class="cu-warn">⚠️ ${w}</div>`).join('') : '';

    ui.run.disabled = !items.length || running;
    ui.stage.hidden = !items.length;
}

function renderAll() {
    renderList();
    renderSummary();
    // Условные строки настроек (битрейт, WMA) следят за списком: трек
    // добавили, убрали крестиком или очистили список целиком.
    syncVisibility();
}

function setBusy(on, text) {
    ui.busy.hidden = !on;
    if (text) ui.busyText.textContent = text;
    ui.run.disabled = on || !items.length;
}

function setProgress(done, total, text) {
    ui.progWrap.hidden = false;
    const pct = total ? Math.round(done / total * 100) : 0;
    ui.progBar.style.width = pct + '%';
    ui.progText.textContent = text || (done + ' / ' + total);
}

/* ───────────────────────── громкость: замер и выравнивание ─────────────── */

let audioCtx = null;
function measureCtx() {
    if (!audioCtx) {
        const AC = window.AudioContext || window.webkitAudioContext;
        // 48 кГц фиксируем намеренно: на этой частоте реализация BS.1770
        // сверена с `ffmpeg -filter ebur128`, и менять её под частоту файла
        // значило бы менять то, что проверено.
        audioCtx = new AC({ sampleRate: 48000 });
    }
    return audioCtx;
}

/** Громкость и пик по байтам файла — или null, если браузер его не декодирует. */
async function loudnessOf(u8) {
    try {
        const ab = u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength);
        const decoded = await measureCtx().decodeAudioData(ab);
        const ch = [];
        for (let c = 0; c < Math.min(2, decoded.numberOfChannels); c++) ch.push(decoded.getChannelData(c));
        const r = integratedLoudness(ch, decoded.sampleRate);
        return { lufs: r.lufs, peak: r.peak, duration: decoded.duration };
    } catch (e) {
        return null;
    }
}

async function measureLoudness(it) {
    if (it.lufs != null) return it.lufs;
    const m = await loudnessOf(await bytesOf(it.file));
    if (m) {
        it.lufs = m.lufs;
        it.peak = m.peak;
        if (!it.durationSec) it.durationSec = m.duration;
    } else {
        it.lufs = NaN;                            // «померить не вышло» — не «тишина»
    }
    return it.lufs;
}

/* ──────────────────────────────── производство ─────────────────────────── */

let ffmpegReady = null;
async function ensureFfmpeg() {
    /* ⚠️ `window.FFmpegProcessor` СУЩЕСТВУЕТ ВСЕГДА. Layout ставит заглушку
       (`__sharedModule`), которая складывает вызовы в очередь и отвергает их
       через 15 секунд — она заведена ради усечённых прокси-провайдером
       скриптов. Проверка «если объект есть, значит модуль загружен» поэтому
       ложна: настоящий модуль не грузится вовсе, а тул получает
       «module never loaded (15s timeout)» на каждый файл. Признак настоящего
       модуля — отсутствие метки `__isStub`. */
    const ready = () => (window.FFmpegProcessor && !window.FFmpegProcessor.__isStub);
    if (ready()) return window.FFmpegProcessor;
    if (!ffmpegReady) {
        ffmpegReady = new Promise((res, rej) => {
            const s = document.createElement('script');
            s.src = ui.cfg.ffmpegSrc;
            s.onload = res; s.onerror = rej;
            document.head.appendChild(s);
        });
    }
    await ffmpegReady;
    // Скрипт загружен — но регистрация настоящего объекта происходит внутри
    // него, а очередь заглушки сливается отдельно. Дожидаемся именно смены
    // объекта, а не события onload.
    for (let i = 0; i < 100 && !ready(); i++) await new Promise((r) => setTimeout(r, 50));
    if (!ready()) throw new Error('ffmpeg module did not register');
    return window.FFmpegProcessor;
}

/** Уменьшить обложку до размера, который не тормозит листание по папке. */
async function shrinkCover(cover, maxPx) {
    if (!cover || !cover.data || !cover.data.length) return null;
    try {
        const blob = new Blob([cover.data], { type: cover.mime || 'image/jpeg' });
        const bmp = await createImageBitmap(blob);
        const side = Math.min(maxPx, Math.max(bmp.width, bmp.height));
        const scale = side / Math.max(bmp.width, bmp.height);
        const w = Math.max(1, Math.round(bmp.width * scale));
        const h = Math.max(1, Math.round(bmp.height * scale));
        const cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        cv.getContext('2d').drawImage(bmp, 0, 0, w, h);
        bmp.close && bmp.close();
        const out = await new Promise((r) => cv.toBlob(r, 'image/jpeg', 0.82));
        if (!out) return null;
        return { mime: 'image/jpeg', data: new Uint8Array(await out.arrayBuffer()) };
    } catch (e) { return null; }
}

/**
 * Один трек → готовые байты. Здесь и живёт разделение «побайтово / перекодек».
 */
async function produce(it, targetGainDb) {
    if (it.verdict.out === 'wma') return produceWma(it);
    const { u8, info } = await produceStream(it, targetGainDb, it.verdict, settings.profile, { fallback: true });

    // Обложка.
    let cover = null;
    if (settings.cover !== 'drop') {
        /* У перекодированного трека в байтах обложки нет: FFmpeg шёл с -vn, и до
           23.09.2026 её «доставали» из выхода, где её и быть не могло, — FLAC,
           M4A и Ogg приезжали на флешку без картинки при настройке «Уменьшить
           до 300 пикселей». Берём её у ИСТОЧНИКА общим разборщиком. */
        let src = null;
        if (it.verdict && it.verdict.lane === 'transcode') {
            const pic = await readCover(it.file).catch(() => null);
            if (pic) src = { mime: pic.mime, data: pic.bytes };
        } else {
            src = extractCover(u8, info);
        }
        if (src) {
            const P = PROFILES[settings.profile];
            cover = (settings.cover === 'shrink' || src.data.length > P.maxCoverBytes)
                ? await shrinkCover(src, 300)
                : src;
        }
    }

    const r = retagMp3(u8, it.tags, {
        mode: settings.mode,
        cover,
        id3v1: true,
        bounds: info,
    });
    assertWritable(r.bytes, it, 'run');
    it.lostChars = r.lost;
    return new Blob([r.bytes], { type: 'audio/mpeg' });
}

/**
 * Последний рубеж перед флешкой: файл с именем .mp3 обязан начинаться потоком
 * MPEG сразу за тегом. Отказ здесь — не ошибка трека, а ошибка тула: какая-то
 * ветка собрала то, что не сыграет, и записать это молча значило бы отдать
 * человеку файл, который устройство даже не покажет в списке.
 */
function assertWritable(bytes, it, where) {
    if (isWritableMp3(bytes)) return;
    ae('tool_error', { action: 'output_not_mpeg', where, ext: it.ext, lane: it.verdict ? it.verdict.lane : '' });
    throw new Error('output_not_mpeg');
}

/**
 * WMA, оставленный как есть (настройка «Оставить WMA»). Звук не трогается:
 * переписывается только заголовок ASF с тегами — в кодировке названий,
 * выбранной для всего сборника, — а обложка решается той же настройкой, что
 * у MP3. Перед флешкой — свой последний рубеж: всё после заголовка обязано
 * совпасть с источником байт в байт.
 */
async function produceWma(it) {
    const u8 = await bytesOf(it.file);
    const P = PROFILES[settings.profile];
    let cover = null;
    let keepPictures = false;
    if (settings.cover === 'keep' && it.coverBytes <= P.maxCoverBytes) keepPictures = true;
    else if (settings.cover !== 'drop') {
        const pic = await readCover(it.file).catch(() => null);
        if (pic) cover = await shrinkCover({ mime: pic.mime, data: pic.bytes }, 300);
    }
    const r = rewriteWmaTags(u8, it.tags, { mode: settings.mode, cover, keepPictures });
    assertWritableWma(r.bytes, u8, it, 'run');
    it.lostChars = r.lost;
    return new Blob([r.bytes], { type: 'audio/x-ms-wma' });
}

function assertWritableWma(bytes, src, it, where) {
    if (isWritableWma(bytes, src)) return;
    ae('tool_error', { action: 'output_not_wma', where, codec: it.wma ? it.wma.codec : '' });
    throw new Error('output_not_wma');
}

/**
 * Трек → MPEG-поток, который устройство сыграет: здесь и живёт разделение
 * «побайтово / перекодек». Теги сюда не входят — их кладёт вызывающий: запись
 * флешки ставит теги трека, калибровочный набор — три тестовых.
 *
 * ⚠️ Это ОДНА дверь для обоих. Пока у калибровки была своя, она умела только
 * переподписать MP3 — и набор из FLAC уехал на флешку хвостом FLAC под именем
 * .mp3 (обращение 20.09.2026: «в магнитоле файлы не видны, AIMP — decoder was
 * not found for this format»).
 */
async function produceStream(it, targetGainDb, verdict, profile, opts) {
    let u8 = await bytesOf(it.file);
    let info = it.info;
    let lossless = verdict.lane !== 'transcode';

    if (!lossless) {
        const F = await ensureFfmpeg();
        const args = transcodeArgs({ profile, bitrate: settings.bitrate });
        try {
            const res = await F.process(it.file, args, 'car.mp3');
            u8 = new Uint8Array(await res.blob.arrayBuffer());
            info = analyzeMp3(u8);
            if (!info) throw new Error('transcode produced no MPEG stream');
        } catch (err) {
            /* FFmpeg-WASM падает на повреждённых MP3 (сессия 18.09.2026: 12 из
               55 треков — «non monotonically increasing dts», «Invalid data»,
               крах движка), и трек просто не доезжал до флешки, а отчёт говорил
               «не удалось: 12» без имён. Если исходник — MPEG-поток, его лучше
               записать как есть (теги, ID3v1, порядок — всё прежнее) и честно
               сказать об этом, чем потерять. Не MPEG (FLAC и т. п.) под именем
               .mp3 писать нельзя — там отказ остаётся отказом. */
            if (!(opts && opts.fallback) || !it.info) throw err;
            const why = String(err && err.message || err).slice(0, 120);
            ae('feature_use', { action: 'transcode_fallback', reasons: verdict.reasons.join(','), err: why.slice(0, 90) });
            it.fallback = why;
            u8 = await bytesOf(it.file);
            if (u8.length !== it.size) u8 = new Uint8Array(await it.file.arrayBuffer());
            info = it.info;
            lossless = true;
        }
        /* Громкость перекодированного трека, который браузер не смог измерить
           в исходнике (WMA, APE, WavPack — decodeAudioData их не знает), — по
           готовому MP3. Без этого «Выровнять громкость» у таких треков молча
           не делало ничего: цель была 0 дБ. */
        if (!lossless && settings.level && !(it.lufs != null && isFinite(it.lufs))) {
            const m = await loudnessOf(u8);
            if (m && isFinite(m.lufs)) {
                it.lufs = m.lufs;
                it.peak = m.peak;
                it.lufsFrom = 'output';
                targetGainDb = ROAD_TARGET_LUFS - m.lufs;
            }
        }
    }
    if (lossless) {
        // Мусор МЕЖДУ кадрами — первым: он меняет и границы, и заголовок длины.
        if (verdict.fixes.indexOf('inner_junk') >= 0) {
            const c = compactStream(u8);
            if (c) { u8 = c; info = analyzeMp3(u8) || info; }
        }
        // Заголовок VBR — до всего остального: он меняет границы потока.
        if (verdict.fixes.indexOf('add_xing') >= 0) {
            const withXing = ensureVbrHeader(u8);
            if (withXing) { u8 = withXing; info = analyzeMp3(u8); }
        }
    }

    // Громкость — по кадрам, без потерь. Делается ДО тегов: гейн ходит по
    // аудио, теги к нему не относятся, но повторный разбор дешевле не станет.
    if (settings.level && isFinite(targetGainDb) && Math.abs(targetGainDb) >= GAIN_STEP_DB / 2) {
        /* Потолок подъёма считается по ИЗМЕРЕННОМУ пику, а не берётся общей
           константой. Общая константа (+6 дБ) — это осторожность вслепую: она
           одинаково душит и запись, ужатую в потолок, и тихий концертный
           рип с пиком −30 dBFS, которому подъём безопасен весь. Пик у нас уже
           есть — его вернул тот же замер громкости, что дал и цель.
           Запас 1 дБ: декодер MP3 может выйти чуть выше пика исходного PCM. */
        const headroom = (it.peak > 0 && it.peak < 1)
            ? Math.max(0, -20 * Math.log10(it.peak) - 1)
            : 0;
        const g = applyGainDb(u8, targetGainDb, { maxUp: Math.min(24, headroom) });
        if (g) { u8 = g.bytes; info = analyzeMp3(u8); it.appliedDb = g.appliedDb; }
    }

    return { u8, info };
}

/* ────────────────────────────────── запись ─────────────────────────────── */

async function run() {
    if (running || !items.length) return;

    /* Кап. Единица — СОБРАННАЯ ФЛЕШКА, а не файл: пофайловая единица с числом 2
       отвергала бы собственную минимальную работу тула (смысл в том, чтобы
       разложить сборник целиком). Составной ключ повторяет решение
       /split-screen; повтор того же набора в тот же день бесплатен. */
    let limit = null;
    if (window.__toolLimit) {
        const key = 'set:' + items.map((it) => it.limitKey).sort().join('|');
        limit = window.__toolLimit.begin(SLUG, [key]);
        if (!limit) return;
    }

    running = true;
    ui.run.disabled = true;
    ui.report.hidden = true;
    let plan = buildCurrentPlan();
    let byId = Object.create(null);
    for (const e of plan.entries) byId[e.id] = e;

    const t0 = Date.now();
    let written = 0, failed = 0, transcoded = 0, skipped = 0, wmaKept = 0, fallback = 0, levelLate = 0;
    let noSpace = 0, noSpaceBytes = 0;
    let batch = null;
    // Итог прошлого прогона — не итог этого: пометки строк считаются заново.
    for (const it of items) { if (it.status !== 'pending') it.status = 'pending'; it.error = ''; it.fallback = ''; }

    try {
        /* Папку просим ПЕРВЫМ await: showDirectoryPicker живёт на пользовательской
           активации, и любая работа до него эту активацию тратит. */
        batch = await window.__batchDeliver.openBatch({
            zipName: 'car-usb.zip',
            slug: SLUG,
            total: items.length + (settings.playlist ? 1 : 0),
            callerReports: true,          // что не легло — поимённо в отчёте тула
        });

        if (batch.mode !== 'folder') {
            window.__toast && window.__toast(t('warn_zip_order'), 'warning');
            ae('feature_use', { action: 'zip_lane' });
        }

        /* Флешку дописывают. Новые файлы FAT ставит ПОСЛЕ существующих (или на
           места удалённых), а номера в именах должны продолжить уже записанные —
           иначе вторые «01» встанут между старыми треками у устройства, которое
           сортирует по имени. */
        let dest = null, numberFrom = null;
        if (batch.mode === 'folder' && batch.dir) {
            dest = await lookAtDestination(batch.dir, plan);
            if (settings.numbering && Object.keys(dest.numberFrom).length) {
                numberFrom = dest.numberFrom;
                plan = buildCurrentPlan(numberFrom);
                byId = Object.create(null);
                for (const e of plan.entries) byId[e.id] = e;
            }
        }

        /* Замер громкости — ВНУТРИ записи, а не отдельным проходом перед ней.
           Цель у каждого трека своя и постоянная (ROAD_TARGET_LUFS), знать весь
           сборник для неё не нужно. Замер 26.09.2026 на настоящей странице
           (24 MP3 по 4 мин + 16 WMA): проход замера 46 с из 57, запись 8 с — у
           заявителя на 229 MP3 это минуты ожидания до первого записанного файла.
           Теперь трек меряется перед своей записью, а следующие — параллельно,
           пока пишется текущий (декод идёт на пуле потоков браузера, запись на
           носитель — в процессе браузера). */
        const toMeasure = settings.level ? items.filter((it) => !it.readErr && it.verdict && it.verdict.out === 'mp3') : [];
        /* Сколько треков декодировать наперёд: каждый держит ~90 МБ PCM на время
           замера. Память — у общей двери (__memBudget): на iPhone deviceMemory нет
           вовсе, и своя догадка разошлась бы с остальными; вкладка с тесным
           потолком (constrained) меряет по одному. */
        const MB = window.__memBudget;
        const mem = MB && typeof MB.gb === 'function' ? MB.gb() : 4;
        const tight = !!(MB && typeof MB.constrained === 'function' && MB.constrained());
        const cores = navigator.hardwareConcurrency || 2;
        const AHEAD = tight ? 1 : Math.max(1, Math.min(3, cores >> 1, mem >= 8 ? 3 : mem >= 4 ? 2 : 1));
        const inFlight = new Map();
        let nextMeasure = 0;
        const kick = () => {
            while (inFlight.size < AHEAD && nextMeasure < toMeasure.length) {
                const m = toMeasure[nextMeasure++];
                /* Замер — не повод ронять запуск. До 25.09.2026 один файл, который
                   браузер перестал отдавать, выбрасывал FILE_LOST во внешний catch:
                   три запуска подряд без единого записанного трека. */
                inFlight.set(m.id, measureLoudness(m).catch((e) => { m.lufs = NaN; m.readErr = String(e && e.message || e); }));
            }
        };
        /* Куда уходит время записи — по фазам: ожидание замера (то, что не
           спряталось за записью), подготовка файла и сама запись на носитель. */
        let msMeasure = 0, msProduce = 0, msWrite = 0, bytesOut = 0;
        const measured = new Set(toMeasure.map((m) => m.id));
        const gainFor = async (it) => {
            if (!measured.has(it.id)) return 0;
            kick();                   // пишем по порядку, поэтому окно всегда начинается с этого трека
            const tm = Date.now();
            await inFlight.get(it.id);
            msMeasure += Date.now() - tm;
            inFlight.delete(it.id);
            kick();
            return (it.lufs != null && isFinite(it.lufs)) ? (ROAD_TARGET_LUFS - it.lufs) : 0;
        };
        kick();

        let i = 0;
        for (const it of items) {
            const e = byId[it.id];
            if (isSkipped(it)) { skipped++; i++; continue; }
            setProgress(i, items.length, t('prog_write', { i: i + 1, n: items.length, name: it.name }));
            const gainDb = noSpace ? 0 : await gainFor(it);
            if (it.readErr || !e) {
                it.status = 'error';
                it.error = it.readErr || 'unreadable';
                failed++;
                i++;
                continue;
            }
            /* Флешка кончилась — остальное на неё уже не ляжет: пишутся не
               «ошибки», а честное «не поместилось», и каждый следующий файл не
               оставляет на носителе пустую запись с именем трека. */
            if (noSpace) { it.status = 'error'; it.error = NO_SPACE; noSpace++; noSpaceBytes += it.size; i++; continue; }
            try {
                const tp = Date.now();
                const blob = await produce(it, gainDb);
                const tw = Date.now();
                msProduce += tw - tp;
                try { await batch.write(e.path, blob); }
                catch (we) {
                    if (we && we.name === 'QuotaExceededError') { we.noSpace = true; }
                    throw we;
                }
                msWrite += Date.now() - tw;
                bytesOut += blob.size || 0;
                it.status = 'done';
                written++;
                if (it.fallback) fallback++;
                else if (it.verdict.lane === 'transcode') transcoded++;
                if (it.verdict.out === 'wma') wmaKept++;
                if (it.lufsFrom === 'output') levelLate++;
            } catch (err) {
                it.status = 'error';
                if (err && err.noSpace) {
                    it.error = NO_SPACE;
                    noSpace = 1;
                    noSpaceBytes = it.size;
                    ae('tool_error', { action: 'no_space', written, left: items.length - i });
                } else {
                    it.error = String(err && err.message || err);
                    failed++;
                    ae('tool_error', { action: 'produce_failed', ext: it.ext, lane: it.verdict ? it.verdict.lane : '', err: it.error.slice(0, 90) });
                }
            }
            i++;
            await new Promise((r) => setTimeout(r, 0));
        }

        if (settings.playlist && written) {
            try {
                const m3u = buildM3u(plan.entries.filter((e) => {
                    const it = items.find((x) => x.id === e.id);
                    return it && it.status === 'done';
                }), settings);
                // .m3u пишется в кодировке тегов: устройство, не понимающее
                // UTF-8 в тегах, не поймёт его и здесь.
                // Имена в режиме Latin-1 уже сведены к знакам ≤ 0xFF (safeName).
                const enc = settings.mode === 'utf16' ? new TextEncoder().encode(m3u)
                    : settings.mode === 'latin1'
                        ? Uint8Array.from([...m3u].map((ch) => (latin1Fits(ch) ? ch.codePointAt(0) : 0x3f)))
                        : (await import('./cp1251.js')).encode(m3u).bytes;
                /* Дозапись: прежний плейлист ДОПОЛНЯЕТСЯ новыми строками, а не
                   заменяется списком одних новых треков — иначе магнитола, которая
                   играет по плейлисту, «теряет» всё записанное раньше. */
                const prior = batch.dir ? await readPriorPlaylist(batch.dir, '00 - playlist.m3u') : null;
                const body = prior ? appendM3u(prior, enc) : enc;
                await batch.write('00 - playlist.m3u', new Blob([body], { type: 'audio/x-mpegurl' }));
            } catch (e) { /* плейлист — приятное дополнение, не результат */ }
        }

        const res = await batch.finish();
        showReport({ written, failed, transcoded, skipped, wmaKept, fallback, noSpace, noSpaceBytes, dest, numberFrom, via: (res && res.via) || batch.mode, ms: Date.now() - t0 });
        if (limit && written) limit.commit();
        else if (limit) limit.abort();
        stage('written', { n: written, via: batch.mode });
        ae('tool_complete', {
            n: items.length, written, failed, transcoded, skipped, wma_kept: wmaKept, fallback, level_late: levelLate,
            no_space: noSpace, unreadable: items.filter((x) => x.readErr).length,
            wma: items.some((x) => x.wma) ? settings.wma : '',
            mode: settings.mode, profile: settings.profile, level: settings.level ? 1 : 0,
            folders: settings.folders, order: settings.order, name_from: settings.nameFrom, numbering: settings.numbering ? 1 : 0,
            dest_files: dest ? dest.files : -1, dest_dirs: dest ? dest.dirs : -1, num_cont: numberFrom ? 1 : 0,
            ms_measure: msMeasure, ms_produce: msProduce, ms_write: msWrite, mb: Math.round(bytesOut / 1048576),
            via: batch.mode, ms: Date.now() - t0,
        });
    } catch (err) {
        if (limit) limit.abort();
        if (batch && batch.abort) try { batch.abort(); } catch (e) {}
        window.__handleError ? window.__handleError(err, SLUG) : (window.__toast && window.__toast(String(err), 'error'));
        ae('tool_error', { action: 'run_failed', err: String(err && err.message || err).slice(0, 120) });
    } finally {
        running = false;
        ui.progWrap.hidden = true;
        renderAll();
    }
}

function showReport(r) {
    ui.report.hidden = false;
    const lines = [];
    lines.push(`<div class="cu-rep-title">✅ ${t('rep_title', { n: r.written })}</div>`);
    const facts = [];
    facts.push(t('rep_lossless', { n: r.written - r.transcoded }));
    if (r.transcoded) facts.push(t('rep_transcoded', { n: r.transcoded }));
    if (r.failed) facts.push(t('rep_failed', { n: r.failed }));
    facts.push(t('rep_seconds', { s: (r.ms / 1000).toFixed(1) }));
    lines.push(`<div class="cu-rep-facts">${facts.join(' · ')}</div>`);
    /* КАКИЕ треки не легли — поимённо. До 25.09.2026 отчёт говорил только
       «не удалось: 12», строки таблицы ничем не отличались, и человек узнавал
       о пропаже уже в машине, на 55-трековой флешке. */
    if (r.failed) {
        const names = items.filter((it) => it.status === 'error' && it.error !== NO_SPACE).map((it) => it.name);
        const shown = names.slice(0, 10).map(escapeHtml).join(', ') + (names.length > 10 ? ', …' : '');
        lines.push(`<div class="cu-warn">⚠️ ${t('rep_failed_names', { names: shown })}</div>`);
    }
    /* Флешка кончилась раньше списка: это не «повреждённые файлы», а размер
       носителя, и совет другой. */
    if (r.noSpace) lines.push(`<div class="cu-warn">⚠️ ${t('rep_no_space', { n: r.noSpace, s: fmtBytes(r.noSpaceBytes || 0) })}</div>`);
    if (r.fallback) lines.push(`<div class="cu-warn">⚠️ ${t('rep_fallback', { n: r.fallback })}</div>`);
    // 'folder_fail' — тоже папка: часть файлов на флешке, распаковывать нечего.
    lines.push(`<div class="cu-rep-next">${t(String(r.via).indexOf('folder') === 0 ? 'rep_next_folder' : 'rep_next_zip')}</div>`);
    /* Дозапись на уже заполненную флешку: сказать, КУДА легли новые треки и
       чем проверить. FAT ставит новые записи после старых — или на места
       удалённых, и тогда порядок сбивается; видно это только в самом каталоге. */
    if (r.dest && (r.dest.files || r.dest.dirs)) {
        lines.push(`<div class="cu-rep-next">${t('rep_dest_had', { files: r.dest.files, dirs: r.dest.dirs })}${r.numberFrom ? ' ' + t('rep_numbering_continued') : ''}</div>`);
    }
    /* Понимает ли магнитола WMA, тул не знает — знает человек (или узнает в
       машине). Поэтому отчёт говорит, что проверить и что делать, если нет. */
    if (r.wmaKept) lines.push(`<div class="cu-rep-next">${t('rep_wma_kept', { n: r.wmaKept })}</div>`);
    if (r.skipped) lines.push(`<div class="cu-warn">⚠️ ${t('rep_skipped', { n: r.skipped })}</div>`);
    const lost = [];
    for (const it of items) for (const c of (it.lostChars || [])) if (lost.indexOf(c) < 0) lost.push(c);
    if (lost.length) lines.push(`<div class="cu-warn">⚠️ ${t('rep_lost', { chars: lost.slice(0, 12).join(' ') })}</div>`);
    ui.report.innerHTML = lines.join('');
}

/* ──────────────────── калибровочный набор: спросить машину ─────────────── */

/**
 * Три копии одного трека с тремя кодировками тегов и ASCII-именами файлов.
 * Из браузера НЕЛЬЗЯ узнать, какую кодировку понимает конкретная прошивка —
 * это её свойство, а не свойство файла. Поэтому вместо догадки тул задаёт
 * вопрос устройству: человек вставляет флешку и смотрит, какой из трёх треков
 * подписан по-человечески.
 *
 * Поток всех трёх — самый совместимый (профиль 'old': MPEG-1 Layer III, CBR,
 * 44.1 кГц), независимо от выбранного профиля. Вопрос набора — ТОЛЬКО про
 * кодировку тегов; если бы формат потока был второй переменной, «не видно ни
 * одного» не отвечало бы ни на что. Трек, который под этот профиль уже годится,
 * берётся без перекодирования (мгновенно), остальные идут через ту же дверь
 * производства, что и сама запись флешки.
 */
async function writeCalibration() {
    if (running) return;
    /* Набор — всегда MP3 (вопрос у него только про кодировку тегов), поэтому
       настройку WMA он не читает; файл с DRM в источники не годится вовсе. */
    const verdictOf = (it) => decide(it.info, {
        ext: it.ext, sizeBytes: it.size, coverBytes: it.coverBytes, wma: it.wma || null,
    }, { profile: 'old', mode: settings.mode, levelVolume: false });
    const usable = items.filter((it) => !it.readErr && it.status !== 'error' && verdictOf(it).lane !== 'skip');
    if (!usable.length) { window.__toast && window.__toast(t('err_calib_need_track'), 'warning'); return; }
    const src = usable.find((it) => verdictOf(it).lane === 'lossless') || usable[0];
    const verdict = verdictOf(src);

    /* Папку просим ПЕРВЫМ await: showDirectoryPicker живёт на пользовательской
       активации, а перекодирование ниже длится секунды. */
    const batch = await window.__batchDeliver.openBatch({ zipName: 'car-encoding-test.zip', slug: SLUG, total: 3 });
    running = true;
    ui.run.disabled = true;
    showNearTools(`<div class="cu-busy"><span class="cu-spin"></span><span>${t('busy_calib')}</span></div>`);
    try {
        const { u8, info } = await produceStream(src, 0, verdict, 'old');
        /* Второй файл — однобайтный режим АЛФАВИТА СТРАНИЦЫ: русская и
           украинская проверяют кириллицу (cp1251), остальные — западноевропейский
           Latin-1. К названию добавлена «проба» с буквами этого алфавита: до
           23.09.2026 у английской страницы все три файла назывались одинаково
           «Encoding test N», и сравнивать на экране магнитолы было нечего. */
        const second = ui.cfg.calibSecond === 'cp1251' ? ['cp1251', '2 - CYRILLIC'] : ['latin1', '2 - WESTERN'];
        const modes = [['ascii', '1 - UNIVERSAL'], second, ['utf16', '3 - UNICODE']];
        const probe = t('calib_probe');
        for (const [mode, base] of modes) {
            const tags = {
                title: t('calib_title', { n: base.slice(0, 1) }) + (probe && probe !== 'calib_probe' ? ' · ' + probe : ''),
                artist: t('calib_artist'),
                album: t('calib_album'),
                track: base.slice(0, 1),
            };
            const r = retagMp3(u8, tags, { mode, cover: null, id3v1: true, bounds: info });
            assertWritable(r.bytes, src, 'calibration');
            await batch.write(base + '.mp3', new Blob([r.bytes], { type: 'audio/mpeg' }));
        }
        const res = await batch.finish();
        const via = (res && res.via) || batch.mode;
        // Запись на флешку сорвалась (носитель вынули, место кончилось) — это
        // тоже «набора нет», и отвечать надо тем же, а не «записан».
        if (via !== 'folder' && via !== 'zip') throw new Error('deliver_' + via);
        /* Ответ — в блоке под кнопками, а не тостом: его читают перед тем, как
           идти к машине, и он же говорит, что делать, если не видно ни одного
           файла. Тост исчезает раньше, чем человек дойдёт до парковки. */
        showNearTools(`<div class="cu-rep-title">🧪 ${t('calib_rep_title')}</div>`
            + (via === 'folder' ? '' : `<div class="cu-rep-facts">${t('calib_zip')}</div>`)
            + `<div class="cu-rep-next">${t('calib_done')}</div>`
            + `<div class="cu-rep-next">${t('calib_none')}</div>`);
        ae('feature_use', { action: 'calibration_set', lane: verdict.lane, ext: src.ext, via });
    } catch (e) {
        if (batch.abort) try { batch.abort(); } catch (_) {}
        showNearTools(`<div class="cu-warn">⚠️ ${t('err_calib_failed')}</div>`);
        ae('tool_error', { action: 'calibration_failed', ext: src.ext, lane: verdict.lane, err: String(e && e.message || e).slice(0, 90) });
    } finally {
        running = false;
        ui.run.disabled = !items.length;
    }
}

/** Ответ на кнопку из ряда инструментов — там же, где на неё нажали. */
function showNearTools(html) {
    if (!ui.calib) return;
    ui.calib.hidden = false;
    ui.calib.innerHTML = html;
}

/* ─────────────────────── аудит уже собранной флешки ────────────────────── */

/**
 * Начинается ли за тегом ID3v2 поток MPEG — по окну в 64 КБ, без чтения файла
 * целиком: аудит идёт по флешке на сотни треков, и обложки там по мегабайту.
 * Решает тот же `looksLikeMpegStream`, что и полный разбор при записи.
 */
async function mpegAtHead(file) {
    const head = new Uint8Array(await file.slice(0, 10).arrayBuffer());
    const at = id3v2Length(head);
    const win = new Uint8Array(await file.slice(at, at + 65536).arrayBuffer());
    return looksLikeMpegStream(win);
}

/**
 * «Флешка уже есть, но играет не так» — бесплатный диагноз без переделки.
 * Читает папку и говорит, что именно устройство не переварит: порядок записей
 * каталога, кодировку тегов, число файлов, форматы.
 */
async function auditFolder() {
    if (!window.showDirectoryPicker) { window.__toast && window.__toast(t('err_no_fs_api'), 'warning'); return; }
    let dir;
    try { dir = await window.showDirectoryPicker({ mode: 'read' }); } catch (e) { return; }

    setBusy(true, t('busy_scanning'));
    /* Тот же обход, что у сборки: папка, которую браузер не открыл, — строка
       отчёта, а не конец проверки («System Volume Information» есть в корне
       любой флешки). */
    const scan = await walkFolder(dir, {
        accept: (ext) => AUDIO_EXT.indexOf(ext) >= 0,
        withFiles: false, sizeOthers: false, maxFiles: MAX_FOLDER_FILES,
    });
    const found = scan.files;
    const dirOrder = found.map((f) => f.path);

    const problems = [];
    if (scan.dirErrors.length) problems.push({ k: 'audit_dir_err', n: scan.dirErrors.length, names: namesOf(scan.dirErrors, 5) });
    // 1. Порядок записей каталога против алфавитного — та самая причина
    //    «скопировал по порядку, играет вперемешку».
    const sorted = dirOrder.slice().sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    let outOfOrder = 0;
    for (let i = 0; i < dirOrder.length; i++) if (dirOrder[i] !== sorted[i]) outOfOrder++;
    if (outOfOrder > 0) problems.push({ k: 'audit_order', n: outOfOrder });

    // 2. Форматы, которые устройство не откроет.
    const nonMp3 = found.filter((f) => extOf(f.name) !== 'mp3').length;
    if (nonMp3) problems.push({ k: 'audit_format', n: nonMp3 });

    // 3. Имена, теги и то, что внутри «.mp3».
    let nonAscii = 0, noTagV1 = 0, notMpeg = 0, checked = 0;
    for (const f of found.slice(0, 60)) {
        if (/[^\x20-\x7E]/.test(f.name)) nonAscii++;
        if (extOf(f.name) === 'mp3') {
            try {
                const file = await f.handle.getFile();
                const tail = new Uint8Array(await file.slice(Math.max(0, file.size - 128), file.size).arrayBuffer());
                if (!(tail[0] === 0x54 && tail[1] === 0x41 && tail[2] === 0x47)) noTagV1++;
                if (!(await mpegAtHead(file))) notMpeg++;
                checked++;
            } catch (e) {}
        }
    }
    /* «.mp3», внутри которого не MP3, — единственная причина из списка, при
       которой устройство не сыграет файл ВОВСЕ (часто и не покажет). Раньше
       аудит её не видел: он смотрел расширение и хвост ID3v1, а у испорченного
       калибровочного набора ID3v1 как раз был. */
    if (notMpeg) problems.push({ k: 'audit_not_mpeg', n: notMpeg });
    if (nonAscii) problems.push({ k: 'audit_names', n: nonAscii });
    if (checked && noTagV1) problems.push({ k: 'audit_id3v1', n: noTagV1, of: checked });

    // 4. Переполненные папки.
    const perDir = Object.create(null);
    for (const p of dirOrder) {
        const d = p.indexOf('/') >= 0 ? p.slice(0, p.lastIndexOf('/')) : '';
        perDir[d] = (perDir[d] || 0) + 1;
    }
    const big = Object.keys(perDir).filter((d) => perDir[d] > 255).length;
    if (big) problems.push({ k: 'audit_folder_size', n: big });

    setBusy(false);
    ui.audit.hidden = false;
    ui.audit.innerHTML = `<div class="cu-rep-title">🔎 ${t('audit_title', { n: found.length })}</div>`
        + (problems.length
            ? `<ul class="cu-audit-list">${problems.map((p) => `<li>${t(p.k, p)}</li>`).join('')}</ul>`
              + `<div class="cu-rep-next">${t('audit_fix_hint')}</div>`
            : `<div class="cu-rep-facts">${t('audit_clean')}</div>`);
    ae('feature_use', { action: 'audit', files: found.length, problems: problems.length, not_mpeg: notMpeg, checked });
}

/* ──────────────────────────────── перетаскивание ───────────────────────── */

function wireDrag() {
    let dragId = null;
    ui.tbody.addEventListener('dragstart', (e) => {
        const row = e.target.closest('tr[data-id]');
        if (!row) return;
        dragId = row.dataset.id;
        row.classList.add('is-drag');
        try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', dragId); } catch (_) {}
    });
    ui.tbody.addEventListener('dragend', () => {
        dragId = null;
        ui.tbody.querySelectorAll('.is-drag,.is-over').forEach((r) => r.classList.remove('is-drag', 'is-over'));
    });
    ui.tbody.addEventListener('dragover', (e) => {
        const row = e.target.closest('tr[data-id]');
        if (!row || !dragId) return;
        e.preventDefault();
        ui.tbody.querySelectorAll('.is-over').forEach((r) => r.classList.remove('is-over'));
        row.classList.add('is-over');
    });
    ui.tbody.addEventListener('drop', (e) => {
        const row = e.target.closest('tr[data-id]');
        if (!row || !dragId) return;
        e.preventDefault();
        const from = items.findIndex((x) => x.id === dragId);
        const to = items.findIndex((x) => x.id === row.dataset.id);
        if (from < 0 || to < 0 || from === to) return;
        const [moved] = items.splice(from, 1);
        items.splice(to, 0, moved);
        settings.order = 'as-is';
        if (ui.order) ui.order.value = 'as-is';
        renderAll();
        ae('feature_use', { action: 'reorder' });
    });
}

/* ──────────────────────────────────── монтаж ───────────────────────────── */

export function mount(cfg) {
    ui = {
        cfg,
        lang: cfg.lang || {},
        stage: $('cuStage'), tbody: $('cuBody'), count: $('cuCount'),
        summary: $('cuSummary'), warn: $('cuWarn'), run: $('cuRun'),
        busy: $('cuBusy'), busyText: $('cuBusyText'),
        progWrap: $('cuProgWrap'), progBar: $('cuProgBar'), progText: $('cuProgText'),
        report: $('cuReport'), audit: $('cuAudit'), calib: $('cuCalibBox'), scan: $('cuScan'), order: $('cuOrder'),
    };

    // Настройки: восстановление через общий примитив, а не свой localStorage.
    const bind = (id, key, def, kind) => {
        const el = $(id);
        if (!el) return;
        if (window.__toolSettings) window.__toolSettings.bind(SLUG, el, key, def);
        const read = () => (kind === 'bool' ? el.checked : el.value);
        settings[key] = kind === 'bool' ? (el.checked != null ? el.checked : def) : (el.value || def);
        if (kind === 'num') settings[key] = parseInt(settings[key], 10) || def;
        el.addEventListener('change', () => {
            settings[key] = kind === 'bool' ? el.checked : (kind === 'num' ? (parseInt(read(), 10) || def) : read());
            if (key === 'order') applyOrder();
            if (key === 'profile' || key === 'level' || key === 'wma') items.forEach(recompute);
            if (key === 'wma') ae('feature_use', { action: 'wma_choice', v: settings.wma, n: items.filter((x) => x.wma).length });
            syncVisibility();
            renderAll();
        });
    };

    bind('cuProfile', 'profile', 'common');
    bind('cuMode', 'mode', 'ascii');
    bind('cuOrder', 'order', 'as-is');
    bind('cuNameFrom', 'nameFrom', 'tags');
    bind('cuFolders', 'folders', 'flat');
    bind('cuCover', 'cover', 'shrink');
    bind('cuBitrate', 'bitrate', 192, 'num');
    bind('cuWma', 'wma', 'mp3');
    bind('cuNumbering', 'numbering', true, 'bool');
    bind('cuLevel', 'level', false, 'bool');
    bind('cuPlaylist', 'playlist', true, 'bool');

    /* Через `__bindUploaderFile`, а НЕ голым addEventListener: голая подписка
       гонится с примитивом — если файл выбрали раньше, чем модуль успел
       подписаться, событие уже улетело и тул стоит мёртвый. Примитив
       чинит это ретроактивным повтором и снятием дублей.
       ⚠️ Колбэк зовётся как (File, detail) — НЕ событием; множественный
       выбор живёт в detail.files. */
    const zone = $('cuUpload');
    if (zone) {
        window.__bindUploaderFile(zone, (file, detail) => {
            const d = detail || {};
            addFiles(d.files && d.files.length ? d.files : (file ? [file] : []));
        });
    }

    const on = (id, fn) => { const el = $(id); if (el) el.addEventListener('click', fn); };
    on('cuRun', run);
    on('cuImportFolder', importFolder);
    on('cuAuditBtn', auditFolder);
    on('cuCalib', writeCalibration);
    on('cuClear', () => { items = []; renderAll(); ui.report.hidden = true; if (ui.scan) ui.scan.hidden = true; });

    ui.tbody.addEventListener('click', (e) => {
        const id = e.target && e.target.dataset && e.target.dataset.del;
        if (!id) return;
        items = items.filter((x) => x.id !== id);
        renderAll();
    });

    wireDrag();
    syncVisibility();
    renderAll();

    // Папочная запись — главная дорога тула, и её наличие меняет обещание.
    // Сказать об этом надо ДО работы, а не в отчёте.
    if (!window.__batchDeliver || !window.__batchDeliver.folderAvailable()) {
        const note = $('cuNoFolder');
        if (note) note.hidden = false;
    }
}

function syncVisibility() {
    const show = (id, on) => { const el = $(id); if (el) el.hidden = !on; };
    show('cuBitrateRow', items.some((it) => it.verdict && it.verdict.lane === 'transcode'));
    // Выбор про WMA виден, только когда в списке есть WMA: остальным он ни к чему.
    show('cuWmaRow', items.some((it) => it.wma));
    show('cuLevelNote', settings.level);
    /* У WMA, оставленного как есть, громкость не меняется — и это видно ДО
       записи, рядом с галочкой, а не только бейджем в строке. */
    show('cuLevelWmaNote', settings.level && items.some((it) => it.verdict && it.verdict.out === 'wma'));
    // Куда ляжет содержимое выбранной папки — вопрос дозаписи (26.09.2026).
    show('cuFoldersSourceHint', settings.folders === 'source');
}

export const __test = { settings, recompute };

/* Самомонтирование. Модуль подключается через @moduleGraph (версия в ПУТИ, а
   не в query — относительные импорты query не наследуют), поэтому отдельного
   инлайн-скрипта с импортом нет: он потребовал бы второй раз знать тот же
   версионированный URL. */
function boot() {
    const cfg = window.__CAR_USB_CFG;
    if (!cfg || !document.getElementById('cuUpload')) return;
    try { mount(cfg); } catch (e) {
        window.__handleError ? window.__handleError(e, SLUG) : console.error(e);
    }
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
